import React, { useMemo } from 'react';
import Skeleton from '../../ui/Skeleton';
import { cx } from '../../ui/classNames';
import { createSkeletonWidths } from '../../data_table/skeletonWidths';
import { renderTextCell } from './gridColumns';
import {
    watchlistGridClasses as c,
    watchlistGridClassNames as cn,
} from './watchlistGridStyles';
import { activeSelectionRange } from '../spreadsheet/useSpreadsheetSelection';

const FILLER_CELL_CLASS_NAME = cx(c.cell, c.cellFiller, c.filler, cn.cellFiller);
const LEAD_CELL_CLASS_NAME = cx(c.cell, c.cellPinnedLeft, c.cellLead, cn.cell, cn.cellPinned, cn.cellLead);
const FILL_PREVIEW_VALUE_CLASS_NAME = cx(c.fillPreviewValue, cn.fillPreviewValue);
const UNSELECTED_CELL = Object.freeze({ selected: false });

// What a cell shows: its column's `cell` with TanStack's cell context, called
// as a plain function so the elements it returns reconcile across column
// rebuilds. Text cells (columns without a `cell`) also carry a native title
// for truncated values. The single place cell content is produced.
function buildCellContent(row, entry) {
    const { cell: content, meta } = entry.column.columnDef;
    const context = row.getAllCellsByColumnId()[entry.field].getContext();
    const children = typeof content === 'function' ? content(context) : content;
    const cellClassName = typeof meta?.cellClassName === 'function'
        ? meta.cellClassName(context)
        : meta?.cellClassName;
    return {
        children,
        title: content === renderTextCell ? children : undefined,
        className: cx(entry.cellClassName, cellClassName),
    };
}

// Selection look of one cell, from TanStack's resolved selection: whether it
// is in the range, which sides are the range's outer edges, and whether it is
// the active cell (whose id the grid's aria-activedescendant points at).
function cellSelectionState(cell, entry, activeColumnId, cellId) {
    if (!cell) return UNSELECTED_CELL;
    const active = entry.field === activeColumnId;
    const selected = cell.getIsSelected();
    if (!selected && !active) return UNSELECTED_CELL;
    const edges = selected ? cell.getSelectionEdges() : null;
    return {
        selected,
        id: active ? cellId : undefined,
        className: cx(
            selected && c.cellSelected,
            active && c.cellActive,
            edges?.top && c.cellEdgeTop,
            edges?.right && c.cellEdgeRight,
            edges?.bottom && c.cellEdgeBottom,
            edges?.left && c.cellEdgeLeft,
        ),
    };
}

// A cell a fill-handle drag would write shows the value it would get, in
// lighter text, in place of its content.
function fillPreviewContent(content, value) {
    return {
        children: <span className={FILL_PREVIEW_VALUE_CLASS_NAME}>{value || '—'}</span>,
        title: undefined,
        className: cx(content.className, c.cellFillPreview),
    };
}

// Changes only when this row enters or leaves a fill preview's target rows.
function rowFillPreviewKey(preview, rowIndex) {
    return preview && rowIndex >= preview.startIndex && rowIndex <= preview.endIndex ? preview.key : '';
}

function renderGridCell(entry, content, selectionState) {
    return (
        <div
            key={entry.field}
            role="gridcell"
            id={selectionState?.id}
            data-field={entry.field}
            data-colindex={entry.index}
            aria-colindex={entry.index + 1}
            aria-selected={selectionState ? selectionState.selected : undefined}
            className={cx(content.className, selectionState?.className)}
            style={entry.style}
            title={content.title}
        >
            {content.children}
        </div>
    );
}

// A key that changes only when this row's selection look changes: whether it
// holds the active cell, and the column span and top/bottom edges of each
// selected rectangle crossing it. Rows the selection does not touch keep ''.
function rowSelectionKey(ranges, rowId, rowIndex, getBounds) {
    const range = activeSelectionRange(ranges);
    if (!range) return '';
    let key = range.anchorRowId === rowId ? `active:${range.anchorColumnId}` : '';
    getBounds().forEach((bounds) => {
        if (rowIndex < bounds.minRowIndex || rowIndex > bounds.maxRowIndex) return;
        key += `|${bounds.minColumnIndex}-${bounds.maxColumnIndex}`
            + `${rowIndex === bounds.minRowIndex ? 't' : ''}${rowIndex === bounds.maxRowIndex ? 'b' : ''}`;
    });
    return key;
}

// One virtualized body row. Memoized so scrolling re-renders only the rows
// that enter the window; any data, column or width change replaces `row` or
// `columnLayout` and re-renders it. Selection and fill-preview changes
// re-render only the rows whose key changes, reusing their cell content.
function WatchlistGridRow({
    row,
    rowIndex,
    top,
    height,
    className,
    columnLayout,
    selection,
}) {
    const contents = useMemo(
        () => columnLayout.map((entry) => buildCellContent(row, entry)),
        [row, columnLayout],
    );
    const renderCells = (selectionKey, fillPreviewKey) => {
        const range = selectionKey ? activeSelectionRange(selection.readRanges()) : null;
        const activeColumnId = range?.anchorRowId === row.id ? range.anchorColumnId : null;
        const cellsByColumnId = range ? row.getAllCellsByColumnId() : null;
        const preview = fillPreviewKey ? selection.readFillPreview() : null;
        return columnLayout.map((entry, index) => {
            let selectionState = null;
            if (entry.selectable) {
                selectionState = cellsByColumnId
                    ? cellSelectionState(
                        cellsByColumnId[entry.field],
                        entry,
                        activeColumnId,
                        selection.cellDomId(rowIndex, entry.index),
                    )
                    : UNSELECTED_CELL;
            }
            const previewValue = preview?.valueAt(row.id, rowIndex, entry.field);
            const content = previewValue === undefined
                ? contents[index]
                : fillPreviewContent(contents[index], previewValue);
            return renderGridCell(entry, content, selectionState);
        });
    };
    return (
        <div
            role="row"
            data-id={row.id}
            data-rowindex={rowIndex}
            aria-rowindex={rowIndex + 2}
            className={className}
            style={{ transform: `translateY(${top}px)`, height, '--height': `${height}px` }}
        >
            <selection.Subscribe
                source={selection.atom}
                selector={(ranges) => rowSelectionKey(ranges, row.id, rowIndex, selection.getBounds)}
            >
                {(selectionKey) => (
                    <selection.Subscribe
                        source={selection.fillPreviewAtom}
                        selector={(preview) => rowFillPreviewKey(preview, rowIndex)}
                    >
                        {(fillPreviewKey) => renderCells(selectionKey, fillPreviewKey)}
                    </selection.Subscribe>
                )}
            </selection.Subscribe>
            <div role="presentation" className={FILLER_CELL_CLASS_NAME} />
        </div>
    );
}

export default React.memo(WatchlistGridRow);

/**
 * A row after the table's rows (a pending addition, the "+ Add security"
 * row): one cell spanning every column, pinned to the left edge like the
 * identity columns and `leadWidth` wide, then a filler across the rest. It
 * has no row id, so it is never part of the cell selection.
 */
export function WatchlistGridTrailingRow({
    kind,
    rowIndex,
    height,
    columnCount,
    leadWidth,
    className,
    children,
}) {
    return (
        <div
            role="row"
            data-trailing-row={kind}
            aria-rowindex={rowIndex + 2}
            className={className}
            style={{ height, '--height': `${height}px` }}
        >
            <div
                role="gridcell"
                aria-colindex={1}
                aria-colspan={columnCount}
                className={cx(LEAD_CELL_CLASS_NAME, kind === 'pending' && cn.cellLeadPending)}
                style={{ left: 0, width: leadWidth }}
            >
                {children}
            </div>
            <div role="presentation" className={FILLER_CELL_CLASS_NAME} />
        </div>
    );
}

// The loading state: enough placeholder rows to fill the viewport, striped
// like the data rows, with stable pseudo-random text widths (40-80%) per cell.
export function WatchlistGridSkeletonRows({ rowCount, height, columnLayout }) {
    const nextSkeletonWidth = createSkeletonWidths();
    return (
        <div className={cx(c.skeleton, cn.skeleton)} aria-hidden="true">
            {Array.from({ length: rowCount }, (_, rowIndex) => (
                <div
                    key={rowIndex}
                    className={cx(
                        c.row,
                        cn.row,
                        c.rowSkeleton,
                        rowIndex % 2 === 1 && c.rowOdd,
                        rowIndex === 0 && c.rowFirst,
                    )}
                    style={{ '--height': `${height}px` }}
                >
                    {columnLayout.map((entry) => (
                        <div
                            key={entry.field}
                            data-field={entry.field}
                            className={cx(entry.skeletonClassName)}
                            style={{ ...entry.style, height }}
                        >
                            <Skeleton width={nextSkeletonWidth()} />
                        </div>
                    ))}
                    <div className={FILLER_CELL_CLASS_NAME} style={{ height }} />
                </div>
            ))}
        </div>
    );
}
